import { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { removePIIDataModal } from 'src/components/pages/Case/Case.slice';
import { DataDeletionType, useApplyReportDeletionsMutation } from 'src/graphql';
import { Modal } from '../Modal';
import { StepperModal } from '../StepperModal';
import { handleErrorToast, handleSuccessToast } from '../Toast';
import { StyledText } from './RemovePIIDataModal.styles';
import {
  MessagesStep,
  ReportDetailsStep,
  ReporterInfoStep,
  SummaryStep,
} from './Steps';
import { useTextFieldClearAll } from './useTextFieldClearAll';
import { useTextRangeDeleteRequested } from './useTextRangeDeleteRequested';

interface RemovePIIDataModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

enum ModalSteps {
  REPORTER,
  REPORT,
  MESSAGES,
  SUMMARY,
}

const DEFAULT_STEP = ModalSteps.REPORTER;
const DEFAULT_PAYLOAD: DataDeletionType = {
  description: [],
  emotion: [],
  witnesses: [],
  externalNamedOffenders: [],
  preExternalNamedOffendersIds: [],
  preInternalNamedOffendersIds: [],
  postExternalNamedOffendersIds: [],
  postInternalNamedOffendersIds: [],
  reporterId: 0,
  messages: [],
  tags: [],
  auditLogs: [],
  anonymiseReporter: false,
  caseDocumentNotePayload: [],
  reporterNotes: [],
  location: [],
  preSubmissionS3Paths: [],
  postSubmissionS3Paths: [],
  eventDeletions: [],
};
const WARNING_MESSAGE = {
  description:
    'Remove data only to meet local legal or regulatory requirements. Improper deletion may affect reports and breach compliance. Changes won’t be permanent until confirmed in the final step.',
};

export const RemovePIIDataModal = ({
  open,
  setOpen,
}: RemovePIIDataModalProps) => {
  const {
    caseId,
    reportId,
    isReporterAnonymous,
    isReporterKnown,
    reporterNotes,
    reportEvents,
    postSubmissionFiles,
  } = useSelector(removePIIDataModal);
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(ModalSteps.REPORTER);
  const [buttonState, setButtonState] = useState<'default' | 'pending'>(
    'default',
  );

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [payload, setPayload] = useState<DataDeletionType>(DEFAULT_PAYLOAD);

  const hasNoDeletions =
    !payload.eventDeletions?.length &&
    !payload.messages?.length &&
    !payload.reporterNotes?.length &&
    !payload.anonymiseReporter &&
    !payload.postSubmissionS3Paths?.length &&
    !payload.description?.length &&
    !payload.emotion?.length &&
    !payload.witnesses?.length &&
    !payload.location?.length &&
    !payload.preSubmissionS3Paths?.length &&
    !payload.externalNamedOffenders?.length &&
    !payload.preExternalNamedOffendersIds?.length &&
    !payload.preInternalNamedOffendersIds?.length &&
    !payload.postExternalNamedOffendersIds?.length &&
    !payload.postInternalNamedOffendersIds?.length;

  const handleBackButtonClick = () => {
    setActiveStep(() => Math.max(activeStep - 1, 0));
  };

  const handleCloseButtonClick = () => {
    setActiveStep(DEFAULT_STEP);
    setPayload(DEFAULT_PAYLOAD);
  };

  const { mutate: mutationApplyReportDeletions } =
    useApplyReportDeletionsMutation({
      onSuccess: async () => {
        handleSuccessToast({
          toastMessage: 'Report data successfully sanitised',
        });

        queryClient.invalidateQueries(['CaseById']);
        queryClient.invalidateQueries([
          'GetCaseAuditEntries',
          { case: caseId },
        ]);
      },

      onError: () => {
        handleErrorToast({
          toastMessage: 'Error deleting report PII deleted.',
        });
      },
      onSettled: () => {
        setButtonState('default');
        setActiveStep(DEFAULT_STEP);
        setPayload(DEFAULT_PAYLOAD);
        setOpen(false);
      },
    });

  const handleSubmitSuccess = () => {
    setButtonState('pending');
    mutationApplyReportDeletions({
      reportId,
      data: payload,
    });
  };

  const handleContinueButtonClick = () => {
    if (activeStep === ModalSteps.SUMMARY) {
      setOpenConfirmationModal(true);
    } else {
      setActiveStep(
        Math.min(activeStep + 1, Object.keys(ModalSteps).length - 1),
      );
    }
  };

  const handleConfirmationModalConfirm = () => {
    setOpenConfirmationModal(false);
    handleSubmitSuccess();
  };

  const findEventDeletionIndex = useCallback(
    (eventDeletions: Array<{ eventId: string }>, eventId: string) => {
      return eventDeletions.findIndex((event) => event.eventId === eventId);
    },
    [],
  );

  const onTextRangeDeleteRequested = useTextRangeDeleteRequested({
    setPayload,
    reportEvents,
    findEventDeletionIndex,
  });

  const handleTextFieldClearAll = useTextFieldClearAll({
    setPayload,
    reportEvents,
    findEventDeletionIndex,
  });

  const steps = [
    {
      label: 'Reporter Information',
      component: (
        <ReporterInfoStep
          payload={payload}
          setPayload={setPayload}
          isReporterAnonymous={isReporterAnonymous}
          isReporterKnown={isReporterKnown}
          warningMessage={WARNING_MESSAGE}
          reporterNotes={reporterNotes}
          onTextRangeDeleteRequested={onTextRangeDeleteRequested}
          handleTextFieldClearAll={handleTextFieldClearAll}
        />
      ),
    },
    {
      label: 'Report Details',
      component: (
        <ReportDetailsStep
          payload={payload}
          setPayload={setPayload}
          warningMessage={WARNING_MESSAGE}
          reportEvents={reportEvents}
          postSubmissionFiles={postSubmissionFiles}
          onTextRangeDeleteRequested={onTextRangeDeleteRequested}
          handleTextFieldClearAll={handleTextFieldClearAll}
        />
      ),
    },
    {
      label: 'Messages',
      component: (
        <MessagesStep
          warningMessage={WARNING_MESSAGE}
          reportId={reportId}
          payload={payload}
          setPayload={setPayload}
        />
      ),
    },
    {
      label: 'Summary',
      component: (
        <SummaryStep
          reportId={reportId}
          payload={payload}
          reportEvents={reportEvents}
          reporterNotes={reporterNotes}
          postSubmissionFiles={postSubmissionFiles}
        />
      ),
    },
  ];

  return (
    <>
      <StepperModal
        open={open}
        setOpen={setOpen}
        steps={steps}
        activeStep={activeStep}
        data-testid="remove-pii-data-modal"
        isContinueButtonDisabled={
          activeStep === ModalSteps.SUMMARY && hasNoDeletions
        }
        finalStepContinueButtonText="Delete"
        finalStepContinueButtonVariant="error"
        onCloseModal={handleCloseButtonClick}
        onBackButtonClick={handleBackButtonClick}
        onContinueButtonClick={handleContinueButtonClick}
        submitButtonState={buttonState}
        disableCloseOnOutsideClick={true}
        removeChildrenPadding={activeStep === ModalSteps.MESSAGES}
      />
      <Modal
        open={openConfirmationModal}
        setOpen={setOpenConfirmationModal}
        size="small"
        title="Confirm Permanent Data Deletion"
        data-testid="remove-pii-data-modal-confirmation"
        description={
          <>
            <StyledText>This action cannot be undone.</StyledText>
            Once you confirm deletion, all selected data will be permanently
            removed and cannot be recovered.
          </>
        }
        confirmButtonText="Delete Data"
        confirmButtonVariant="error"
        cancelButtonText="Back"
        onCancel={() => setOpenConfirmationModal(false)}
        onConfirm={handleConfirmationModalConfirm}
      />
    </>
  );
};
